import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Avatar } from "../components/badges";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6 fade-in max-w-3xl" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Access Management</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Team</h1>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1" /> Add User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Add User</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-1.5"><Label>Name</Label>
                  <Input data-testid="new-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                <div className="space-y-1.5"><Label>Email</Label>
                  <Input type="email" data-testid="new-user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                <div className="space-y-1.5"><Label>Password</Label>
                  <Input type="password" data-testid="new-user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
                <div className="space-y-1.5"><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select></div>
                <Button type="submit" data-testid="create-user-submit-btn" className="w-full">Create</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="divide-y divide-border">
        {users.map((u) => (
          <div key={u.id} className="p-4 flex items-center gap-3" data-testid={`team-member-row-${u.email.split("@")[0]}`}>
            <Avatar name={u.name} size="md" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{u.name} {u.id === user.id && <span className="text-xs text-muted-foreground">(you)</span>}</p>
              <p className="text-xs text-muted-foreground font-mono">{u.email}</p>
            </div>
            {isAdmin && u.id !== user.id ? (
              <>
                <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                  <SelectTrigger className="w-32" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" data-testid="delete-user-btn" onClick={() => remove(u.id)}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </>
            ) : (
              <Badge variant="outline" className="font-mono text-[10px] uppercase">{u.role}</Badge>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}
